import { useEffect, useState } from "react";
import { fetchRecentReports, type DailyReportSummary } from "../lib/api";
import { StatusOrb, statusLabel } from "../components/StatusOrb";
import { formatDateVi } from "../lib/format";

export function Trends() {
  const [reports, setReports] = useState<DailyReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentReports(60)
      .then(setReports)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const counts = reports.reduce(
    (acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div>
      <section className="bg-canvas px-6 py-section md:px-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-displayLg">60 ngày gần đây</h2>
          <p className="mt-4 text-bodyApple text-ink-muted80">
            Phân bố trạng thái theo ngày — anh đang ở đâu?
          </p>
          {loading && <div className="mt-12 h-32 animate-pulse rounded-lg bg-canvas-parchment" />}
          {error && <p className="mt-6 text-captionApple text-[#a84a3a]">{error}</p>}
          {!loading && !error && (
            <>
              <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
                {(["normal", "note", "attention", "urgent"] as const).map((s) => (
                  <div key={s} className="rounded-lg border border-hairline bg-canvas-pearl p-6">
                    <div className="flex items-center gap-3">
                      <StatusOrb status={s} />
                      <span className="text-captionStrong text-ink-muted80">
                        {statusLabel(s)}
                      </span>
                    </div>
                    <p className="mt-4 tnum text-displayMd">{counts[s] ?? 0}</p>
                  </div>
                ))}
              </div>

              <div className="mt-16">
                <h3 className="text-tagline">Heatmap</h3>
                <div className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(14px,1fr))] gap-1">
                  {reports.map((r) => (
                    <div
                      key={r.date_local}
                      title={`${r.date_local} · ${statusLabel(r.status)} · ${r.headline}`}
                      className="aspect-square rounded-xs"
                      style={{
                        backgroundColor:
                          r.status === "normal"
                            ? "#0066cc"
                            : r.status === "note"
                              ? "#cccccc"
                              : r.status === "attention"
                                ? "#1d1d1f"
                                : "#a84a3a"
                      }}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {reports.length > 0 && (
        <section className="bg-tile-1 px-6 py-section text-white md:px-12">
          <div className="mx-auto max-w-3xl">
            <h3 className="text-displayMd">Tất cả báo cáo</h3>
            <ul className="mt-10 divide-y divide-white/10">
              {reports.map((r) => (
                <li
                  key={r.date_local}
                  className="flex items-center justify-between py-4"
                >
                  <span className="flex items-center gap-4">
                    <StatusOrb status={r.status} />
                    <span className="text-bodyApple">{formatDateVi(r.date_local)}</span>
                  </span>
                  <span className="line-clamp-1 max-w-[60%] text-captionApple text-bodyMuted">
                    {r.headline}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
